import React from 'react';
import clsx from 'clsx';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { makeStyles } from '@mui/styles';
import {
  Box,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import API from 'common/api';
import { PlaceHolderText } from 'components/PlaceholderCard/PlaceholderCard';
import Progress from 'components/Progress/Progress';
import useList from 'hooks/useList';
import useUser from 'hooks/useUser';
import {
  Card, Typography, Button
} from 'bso-ui';
import { isAppAdmin } from 'helpers/permissions';
import DeleteConfirmationDialog from 'components/DeleteConfirmationDialog/DeleteConfirmationDialog';
import { VITE_PORTAL_API } from 'config';
import { del } from 'helpers/query/fetchers';
import { useSnackbar } from 'notistack';
import SnackMessage from 'components/SnackMessage/SnackMessage';
import useConnectErrorHandler from 'helpers/errorHandlers';
import CompanyToolbar from './CompanyToolbar';
import CompanyCreate from './Form/CompanyCreate';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2)
  },
  inner: {
    minWidth: 1050
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  avatar: {
    marginRight: theme.spacing(2)
  },
  actions: {
    justifyContent: 'flex-end'
  }
}));

const CompaniesList = () => {
  const classes = useStyles();
  const height = '80vh';
  const [{ data: userData }] = useUser();
  const query = {
    storeKey: 'company-list',
    apiEndpoint: API.COMPANY,
  };
  const [{
    data, isLoading, isError, refetch, isFetched, isFetching
  }] = useList(query.storeKey, query.apiEndpoint);
  const { enqueueSnackbar } = useSnackbar();
  const { handleError } = useConnectErrorHandler();

  const deleteCompany = (id) => {
    const url = `${VITE_PORTAL_API}${API.COMPANY}${id}/`;
    del(url)
      .then(() => {
        const msg = `Company ${id} has been Deleted.`;

        enqueueSnackbar(msg, {
          content: (key) => <SnackMessage id={key} message={msg} variant="success" />
        });
        refetch();
      })
      .catch((error) => {
        handleError(error);
      });
  };

  if (isError || (isFetched && !data)) {
    return (
      <div style={{ height }}>
        <PlaceHolderText variant="h3" content="Error getting companies" height="100%" />
      </div>
    );
  }

  const getActionButtons = (companyData) => (
    <Stack direction="row" gap={1}>
      {companyData && (userData.is_staff || userData.is_superuser) ? (
        <CompanyCreate
          formType="update"
          title="Update Company"
          data={companyData}
          button={{
            content: 'Update',
            variant: 'standard',
            state: 'outlined',
            color: 'primary',
            disabled: isFetching
          }}
          refetch={refetch}
        />
      ) : (
        <div style={{ paddingTop: '36px' }} />
      )}
      {companyData && userData.is_staff ? (
        <DeleteConfirmationDialog
          id="DeleteCompanyHeader"
          title="Delete Company"
          contentText="Are you sure you want to delete this Company?"
          confirmText="Yes"
        >
          {(confirm) => (
            <Button
              id="DeleteCompanyButton"
              color="primary"
              variant="standard"
              state="outlined"
              disabled={isFetching}
              onClick={confirm(() => deleteCompany(companyData.id))}
            >
              Delete
            </Button>
          )}
        </DeleteConfirmationDialog>
      ) : (
        <div style={{ paddingTop: '36px' }} />
      )}
    </Stack>
  );

  const renderData = () => (
    <Card
      id="companiesListCard"
      className={clsx(classes.root)}
    >
      <CardContent className={classes.content}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ flex: 1 }} />
        </div>
        <PerfectScrollbar>
          <div className={classes.inner}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="subtitle1">Company name</Typography></TableCell>
                  {(userData?.is_staff || userData?.is_superuser)
                  && <TableCell><Typography variant="subtitle1">Actions</Typography></TableCell>}
                </TableRow>
              </TableHead>
              {data && data?.length > 0
                ? (
                  <TableBody>
                    {data.map((company) => (
                      <TableRow
                        className={classes.tableRow}
                        hover
                        key={company.id}
                      >
                        <TableCell>
                          <Typography color="primary" variant="body2" component="span">{company.name}</Typography>
                        </TableCell>
                        {(userData?.is_staff || userData?.is_superuser)
                          && <TableCell>{getActionButtons(company)}</TableCell>}

                      </TableRow>
                    ))}
                  </TableBody>
                )
                : null}
            </Table>
          </div>
        </PerfectScrollbar>
      </CardContent>

    </Card>
  );

  if (userData && (userData.is_staff || isAppAdmin(userData.groups))) {
    return (
      <Box p={3}>
        <CompanyToolbar title="Companies" isFetching={isFetching} refetch={refetch} data={data} userData={userData} />
        {!isLoading
          ? renderData()
          : <Progress />}
      </Box>
    );
  }

  return <PlaceHolderText content="No permissions to view this page" height={height} />;
};

export default CompaniesList;
